/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts"
import { Download, Filter, TrendingUp, PieChartIcon, BarChart3 } from "lucide-react"
import bgImage from "@/assets/favicon.png"
import { Link } from "react-router-dom"
import apiService from "@/services/apiService"
import { getUserInfo } from "@/services/userService"

const COLORS = ["#FF6384","#36A2EB","#ac7f0dff","#4BC0C0","#9966FF","#FF9F40","#C9CBCF","#FF5A5E","#5AD3D1","#FFC870","#A4DE02","#FF6F61","#6B5B95","#88B04B","#F7CAC9","#92A8D1","#955251","#B565A7","#009B77","#DD4124"];


export default function CaseAnalysisReport() {
  const [caseTypeData, setCaseTypeData] = useState([])
  const [sectionAnalysisData, setSectionAnalysisData] = useState([])
  const [policeStations, setPoliceStations] = useState([]);
  const [acts, setActs] = useState([]);
  const queryParams:any = {}
  const userInfo:any = getUserInfo();
  const userId = userInfo?(userInfo.id?userInfo.id:null):null;
  if (userId) queryParams["user_id"] = userId;
  useEffect(() => {
    getCaseTypeCount();
    getActsCount();
    getSectionsCount();
  }, []);

  const getCaseTypeCount = async () => {
    const jsonResponse:any = await apiService.get('general-diaries/case-types-count', queryParams);
    const totalCount = jsonResponse.reduce((a:any,c:any) => a+c.count, 0)
    setCaseTypeData(jsonResponse.map((data:any) => ({
      ...data,
      name:data.caseType,
      percentage:(100*data.count/totalCount).toFixed(2)
    })))
    setPoliceStations([]);
  }
  const getActsCount = async () => {
    const jsonResponse:any = await apiService.get('general-diaries/acts-count', queryParams);
    setActs(jsonResponse.map((act:any) => act.act));
    
  }
  const getSectionsCount = async () => {
    const jsonResponse:any = await apiService.get('general-diaries/sections-count', queryParams);
    console.log(jsonResponse, 'section');
    setSectionAnalysisData(jsonResponse.map((data:any) => ({
      ...data,
      act:"Indian Penal Code",
      caseTypes:[]
    })))
  }
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    policeStation: "",
    act: "",
  })

  const exportData = (type: string) => {
    let data: any[] = []
    let filename = ""

    switch (type) {
      case "caseTypes":
        data = caseTypeData
        filename = "case-types-analysis"
        break
      case "sections":
        data = sectionAnalysisData
        filename = "sections-analysis"
        break
      default:
        return
    }

    const headers = Object.keys(data[0]).join(",")
    const csvContent = [
      headers,
      ...data.map((row) =>
        Object.values(row)
          .map((val) => (Array.isArray(val) ? val.join("; ") : val))
          .join(","),
      ),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <Link to="/reports" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
              ← Back to Reports
            </Link>
            <h1 className="text-3xl font-bold text-black">Case Type & Section Analysis Report</h1>
            <p className="text-gray-600 mt-1">Understand frequency and nature of legal actions</p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-black">
                {caseTypeData.reduce((sum:any, item:any) => sum + item.count, 0)}
              </div>
              <p className="text-sm text-gray-600">Total Cases</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">{caseTypeData.length}</div>
              <p className="text-sm text-gray-600">Case Types</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">{sectionAnalysisData.length}</div>
              <p className="text-sm text-gray-600">Legal Sections</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-600">
                {new Set(sectionAnalysisData.map((item:any) => item.act)).size}
              </div>
              <p className="text-sm text-gray-600">Legal Acts</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border border-gray-200 hidden">
          <CardHeader>
            <CardTitle className="text-lg text-black flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">From Date</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">To Date</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Police Station</Label>
                <Select
                  value={filters.policeStation}
                  onValueChange={(value) => setFilters({ ...filters, policeStation: value })}
                >
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select station" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {policeStations.map((station) => (
                      <SelectItem key={station} value={station}>
                        {station}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Legal Act</Label>
                <Select value={filters.act} onValueChange={(value) => setFilters({ ...filters, act: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select act" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {acts.filter((act:any) => typeof act === "string" && act.trim() !== "").map((act) => (
                      <SelectItem key={act} value={act}>
                        {act}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 bg-gray-100">
            <TabsTrigger value="overview" className="data-[state=active]:bg-white">
              Overview
            </TabsTrigger>
            <TabsTrigger value="case-types" className="data-[state=active]:bg-white">
              Case Types
            </TabsTrigger>
            <TabsTrigger value="sections" className="data-[state=active]:bg-white">
              Legal Sections
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg text-black flex items-center gap-2">
                    <PieChartIcon className="h-5 w-5" />
                    Case Type Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={caseTypeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {caseTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}-${entry}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg text-black flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Top 5 Legal Sections
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={sectionAnalysisData.slice(0, 5)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="section" stroke="#666" angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                      <Bar dataKey="count" fill="#000000" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Case Types Tab */}
          <TabsContent value="case-types">
            <Card className="border border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-black">Case Type Analysis</CardTitle>
                <Button
                  onClick={() => exportData("caseTypes")}
                  size="sm"
                  className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={caseTypeData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="caseType" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                      <Bar dataKey="count" fill="#000000" />
                    </BarChart>
                  </ResponsiveContainer>

                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">Case Type</TableHead>
                        <TableHead className="text-black font-semibold">Count</TableHead>
                        <TableHead className="text-black font-semibold">Percentage</TableHead>
                        <TableHead className="text-black font-semibold">Trend</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {caseTypeData.map((item:any, index:number) => (
                        <TableRow key={index} className="hover:bg-gray-50">
                          <TableCell className="font-medium text-black">{item.caseType}</TableCell>
                          <TableCell className="text-gray-600">{item.count}</TableCell>
                          <TableCell className="text-gray-600">{item.percentage}%</TableCell>
                          <TableCell>
                            <Badge className="bg-green-100 text-green-800">
                              <TrendingUp className="h-3 w-3 mr-1" />
                              +5.2%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Legal Sections Tab */}
          <TabsContent value="sections">
            <Card className="border border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-black">Legal Sections Analysis</CardTitle>
                <Button
                  onClick={() => exportData("sections")}
                  size="sm"
                  className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-black font-semibold">Section</TableHead>
                      <TableHead className="text-black font-semibold">Act</TableHead>
                      <TableHead className="text-black font-semibold">Count</TableHead>
                      <TableHead className="text-black font-semibold">Associated Case Types</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sectionAnalysisData.map((item:any, index:number) => (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell className="font-medium text-black">{item.section}</TableCell>
                        <TableCell className="text-gray-600">{item.act}</TableCell>
                        <TableCell className="text-gray-600">{item.count}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {item.caseTypes.map((caseType:any, idx:number) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {caseType}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
